
import React from 'react';
import { AlertTriangle, RefreshCw, Inbox } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { checkApiIssues } from '@/components/faq-serp/utils/errorDiagnostics';

interface ErrorDisplayProps {
  componentName: string;
  error: Error;
  onRetry?: () => void;
  onReport?: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  componentName,
  error,
  onRetry,
  onReport,
}) => {
  const errorDetails = checkApiIssues(error);
  const isApiKeyError = errorDetails.includes('API key');
  const isRateLimitError = errorDetails.includes('Rate limit');

  return (
    <Card className="border-destructive/30 bg-destructive/5 p-6">
      <div className="flex flex-col space-y-4">
        <div className="flex items-start gap-3">
          <div className="bg-destructive/10 p-2 rounded-full">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1 text-destructive">
              {componentName} Error
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {errorDetails || error.message}
            </p>
            
            {isApiKeyError && (
              <div className="text-xs text-muted-foreground bg-background/50 p-3 rounded border border-border mb-4">
                <p className="font-medium mb-1">API Key Issue</p>
                <ul className="list-disc list-inside">
                  <li>Check that your OpenAI API key is valid</li>
                  <li>Ensure your API key has sufficient credits</li>
                  <li>Verify API key permissions in OpenAI dashboard</li>
                </ul>
              </div>
            )}
            
            {isRateLimitError && (
              <div className="text-xs text-muted-foreground bg-background/50 p-3 rounded border border-border mb-4">
                <p className="font-medium mb-1">Rate Limit Exceeded</p>
                <ul className="list-disc list-inside">
                  <li>Please wait a few minutes before trying again</li>
                  <li>Consider upgrading your OpenAI plan for higher limits</li>
                </ul>
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              {onRetry && (
                <Button 
                  onClick={onRetry} 
                  size="sm" 
                  variant="outline" 
                  className="gap-1.5"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
              )}
              {onReport && (
                <Button 
                  onClick={onReport} 
                  size="sm" 
                  variant="ghost" 
                  className="gap-1.5"
                >
                  <Inbox className="h-4 w-4" />
                  Report Issue
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ErrorDisplay;
